import FormControl from '~/common/components/Forms/FormControl';
import FormLabel from '~/common/components/Forms/FormLabel';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import Input from '~/common/components/Inputs/Input';
import PatternFormatInput from '~/common/components/Inputs/InputField/patternFormatInput';
import InputGroup from '~/common/components/Inputs/InputGroup';
import InputRightElement from '~/common/components/Inputs/InputRightElement';
import Textarea from '~/common/components/Texts/Textarea';
import theme from '~/theme/';

const handleInputChange = (e, pattern, onChangeHandler) => {
  const value = e.target.value;
  if (value !== '' && pattern) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) {
      return;
    }
  }
  if (onChangeHandler) onChangeHandler(e);
};

type FormBuilderTextInputProps = {
  name?: string;
  label?: string;
  span?: number;
  onChangeHandler?: (...args: any[]) => any;
  readOnly?: boolean;
  getValues?: (...args: any[]) => any;
  disabled?: boolean;
  isRequired?: boolean;
  validate?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  pattern?: string;
  isInvalid?: boolean;
  tabIndex?: number;
  placeholder?: string;
  dataTestId?: string;
};

type FormBuilderBaseInputProps = {
  type?: string;
  name?: string;
  label?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  readOnly?: boolean;
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  pattern?: string;
  tabIndex?: number;
  placeholder?: string;
  dataTestId?: string;
  isRequired?: boolean;
};

const FormBuilderBaseInput = ({
  type,
  name,
  label,
  disabled,
  isInvalid,
  readOnly,
  getValues,
  onChangeHandler = () => {},
  onBlur = () => {},
  pattern,
  tabIndex,
  placeholder,
  dataTestId,
  isRequired,
}: Readonly<FormBuilderBaseInputProps>) => {
  let backgroundColor: string | undefined = theme.colors?.white;
  if (disabled) backgroundColor = undefined;
  else if (readOnly) backgroundColor = theme.colors?.gray?.[100];

  return (
    <FormControl isInvalid={isInvalid} isRequired={isRequired}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Input
        type={type}
        style={{ backgroundColor }}
        isReadOnly={readOnly ?? false}
        isDisabled={disabled ?? false}
        tabIndex={tabIndex}
        placeholder={placeholder}
        data-testid={dataTestId}
        id={name}
        value={getValues(name) || ''} // in case of null value
        onChange={(e) => handleInputChange(e, pattern, onChangeHandler)}
        onBlur={(e) => onBlur(e.target.value)}
      />
    </FormControl>
  );
};

// TEXT INPUT
export const FormBuilderTextInput = ({
  name,
  label,
  disabled,
  isInvalid,
  readOnly,
  getValues,
  onChangeHandler,
  onBlur,
  pattern,
  tabIndex,
  placeholder,
  dataTestId,
  isRequired = false,
}: Readonly<FormBuilderTextInputProps>) => {
  return (
    <FormBuilderBaseInput
      name={name}
      label={label}
      disabled={disabled}
      isInvalid={isInvalid}
      readOnly={readOnly}
      getValues={getValues}
      onChangeHandler={onChangeHandler}
      onBlur={onBlur}
      pattern={pattern}
      tabIndex={tabIndex}
      placeholder={placeholder}
      dataTestId={dataTestId}
      isRequired={isRequired}
    />
  );
};

type FormBuilderNumberInputProps = {
  name?: string;
  label?: string;
  span?: number;
  onChangeHandler?: (...args: any[]) => any;
  readOnly?: boolean;
  getValues?: (...args: any[]) => any;
  disabled?: boolean;
  isRequired?: boolean;
  validate?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  isInvalid?: boolean;
  tabIndex?: number;
  placeholder?: string;
  pattern?: string;
  dataTestId?: string;
};

// Number Input
export const FormBuilderNumberInput = ({
  name,
  label,
  disabled,
  isInvalid,
  readOnly,
  getValues,
  onChangeHandler,
  onBlur,
  tabIndex,
  placeholder,
  pattern,
  dataTestId,
}: Readonly<FormBuilderNumberInputProps>) => {
  return (
    <FormBuilderBaseInput
      type="number"
      name={name}
      label={label}
      disabled={disabled}
      isInvalid={isInvalid}
      readOnly={readOnly}
      getValues={getValues}
      onChangeHandler={onChangeHandler}
      onBlur={onBlur}
      pattern={pattern}
      tabIndex={tabIndex}
      placeholder={placeholder}
      dataTestId={dataTestId}
    />
  );
};

type FormBuilderTextAreaInputProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  pattern?: string;
  size?: string;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  isInvalid?: boolean;
  readOnly?: boolean;
  onBlur?: (...args: any[]) => any;
  disabled?: boolean;
  tabIndex?: number;
  dataTestId?: string;
};

// Textarea Input
export const FormBuilderTextAreaInput = ({
  name,
  label,
  size,
  resize,
  disabled,
  readOnly,
  isInvalid,
  getValues,
  onChangeHandler,
  onBlur,
  tabIndex,
  placeholder,
  pattern,
  dataTestId,
}: Readonly<FormBuilderTextAreaInputProps>) => {
  return (
    <FormControl isInvalid={isInvalid}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Textarea
        id={name}
        placeholder={placeholder}
        data-testid={dataTestId}
        style={{ backgroundColor: theme.colors?.white }}
        isReadOnly={readOnly ?? false}
        isDisabled={disabled ?? false}
        tabIndex={tabIndex}
        value={getValues(name)}
        size={size ?? 'md'}
        resize={resize ?? 'none'}
        onChange={(e) => handleInputChange(e, pattern, onChangeHandler)}
        onBlur={(e) => onBlur(e.target.value)}
      />
    </FormControl>
  );
};

type FormBuilderCurrencyInputProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  control?: any;
  rules?: any;
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  isInvalid?: boolean;
  onBlur?: (...args: any[]) => any;
  onChange?: (...args: any[]) => any;
  disabled?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  isRequired?: boolean;
  value?: string;
};

// Currency Input
export const FormBuilderCurrencyInput = ({
  name,
  label,
  isInvalid,
  placeholder,
  value,
  onBlur,
  onChange,
  onChangeHandler,
  disabled,
  readOnly,
  dataTestId,
  isRequired,
}: Readonly<FormBuilderCurrencyInputProps>) => {
  return (
    <FormControl isRequired={isRequired}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <CurrencyInput
        id={name}
        data-testid={dataTestId}
        isInvalid={isInvalid}
        value={value}
        placeholder={placeholder}
        onBlur={onBlur}
        handleCurrencyChange={(_, val) => {
          const numericValue = Number(String(val).replaceAll(',', ''));
          onChange?.(Number.isNaN(numericValue) ? '' : numericValue);
          if (typeof onChangeHandler === 'function') {
            onChangeHandler(Number.isNaN(numericValue) ? '' : numericValue);
          }
        }}
        isReadOnly={readOnly}
        isDisabled={disabled}
      />
    </FormControl>
  );
};

type FormBuilderRightElementInputProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  symbol?: string;
  dataType?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  onBlur?: (...args: any[]) => any;
  tabIndex?: number;
};

// Input With Right Symbol
export const FormBuilderRightElementInput = ({
  name,
  label,
  disabled,
  symbol,
  placeholder,
  isInvalid,
  getValues,
  onBlur,
  onChangeHandler,
  dataType,
  tabIndex,
}: FormBuilderRightElementInputProps) => (
  <FormControl tabIndex={tabIndex}>
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <InputGroup>
      <InputRightElement
        pointerEvents="none"
        backgroundColor="#edf2f7"
        borderRadius={'0 0.375rem 0.375rem 0'}
        border={'1px solid'}
        borderColor={'inherit'}
        borderLeft={'none'}
      >
        {symbol}
      </InputRightElement>
      <Input
        id={name}
        name={name}
        isDisabled={disabled}
        placeholder={placeholder}
        isInvalid={isInvalid ?? false}
        tabIndex={tabIndex}
        value={getValues(name)}
        onBlur={(e) => {
          if (onBlur) onBlur(e.target.value);
        }}
        onChange={(e) => {
          if (tabIndex >= 0) onChangeHandler(e.target.value);
        }}
        type={dataType}
        style={{
          backgroundColor: disabled ? undefined : theme.colors?.white,
        }}
      />
    </InputGroup>
  </FormControl>
);

type FormBuilderPatternedInputProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  getValues?: (...args: any[]) => any;
  onChangeHandler?: (...args: any[]) => any;
  dataType?: string;
  disabled?: boolean;
  value?: string;
  allowEmptyFormatting?: boolean;
  pattern?: string;
  mask?: string;
  isInvalid?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  isRequired?: boolean;
};

// Patterned Input
export const FormBuilderPatternedInput = (
  props: FormBuilderPatternedInputProps,
) => {
  const {
    name,
    label,
    value,
    getValues,
    allowEmptyFormatting,
    mask,
    pattern,
    isInvalid,
    onChangeHandler,
    disabled,
    tabIndex,
    placeholder,
    dataTestId,
    isRequired,
  } = props;

  return (
    <FormControl
      tabIndex={tabIndex}
      isInvalid={isInvalid}
      isRequired={isRequired}
    >
      <FormLabel htmlFor={name}>{label}</FormLabel>

      <PatternFormatInput
        tabIndex={tabIndex}
        allowEmptyFormatting={allowEmptyFormatting ?? false}
        name={name}
        id={name}
        data-testid={dataTestId}
        value={getValues ? getValues(name) : value}
        placeholder={placeholder}
        disabled={disabled || tabIndex === -1}
        pattern={pattern}
        isInvalid={isInvalid ?? false}
        mask={mask ?? ' '}
        onChange={(e) => onChangeHandler(e.target.value)}
      />
    </FormControl>
  );
};

type FormBuilderHiddenInputProps = {
  name?: string;
  getValues?: (...args: any[]) => any;
  register?: (...args: any[]) => any;
  value?: string;
};

// Hidden Input
export const FormBuilderHiddenInput = ({
  value,
  getValues,
}: FormBuilderHiddenInputProps) => (
  <input
    type="hidden"
    defaultValue={getValues(value)}
    value={getValues(value)}
  />
);
